import _ from 'underscore';
import classNames from 'classnames';
import React, { useState, useContext, useMemo, useCallback } from 'react';
import {
  Body1,
  Button,
  Divider,
  useRestoreFocusSource,
  useRestoreFocusTarget,
} from '@fluentui/react-components';
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerFooter,
  Drawer,
} from '@fluentui/react-drawer';
import { Dismiss24Regular, AppsAddIn16Regular } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { Card } from './card';
import { useCardSelectorStyles } from './styles/card-selector-styles';
import { CardSelectorProps, CardConfigProps } from './types';

export function CardSelector({
  cards,
  onSave,
}: CardSelectorProps) {
  const styles = useCardSelectorStyles() as any;
  const i18n = useContext(I18nContext) as any;

  const [open, setOpen] = useState(false);
  const onOpenDrawer = () => setOpen(true);
  const [localCardsState, setLocalCardsState] = useState(cards);
  const onCardSelectionChange = useCallback((cardId, isSelected) => {
    const index = _.findIndex(localCardsState, { id: cardId });
    if (index >= 0) {
      const newCards = [...localCardsState];
      newCards[index] = { ...newCards[index], hidden: !isSelected };
      setLocalCardsState(newCards);
    }
  }, [localCardsState]);

  const onSaveClicked = useCallback(() => {
    onSave(localCardsState);
    setOpen(false);
  }, [localCardsState, onSave]);

  const onCancel = useCallback(() => {
    setOpen(false);
    setLocalCardsState(cards);
  }, [cards]);

  const selectedCount = useMemo(() => _.reject(localCardsState, (card: CardConfigProps) => card.hidden)?.length || 0, [localCardsState]);

  // Overlay Drawer will handle focus by default, but inline Drawers need manual focus restoration attributes, if applicable
  const restoreFocusTargetAttributes = useRestoreFocusTarget();
  const restoreFocusSourceAttributes = useRestoreFocusSource();
  const tileLabel = selectedCount === 1
    ? _TL_('{{count}} tile selected')
    : _TL_('{{count}} tiles selected');

  return (
    <div className={classNames('ba-card-selector', styles.container)}>
      <Button
        {...restoreFocusTargetAttributes}
        className="card-selector-button"
        appearance="transparent"
        icon={<AppsAddIn16Regular />}
        onClick={onOpenDrawer}
      >
        {i18n.getString(_TL_('Add or remove tiles'))}
      </Button>
      <Drawer
        {...restoreFocusSourceAttributes}
        className="card-selector-drawer"
        open={open}
        position="end"
        onOpenChange={onCancel}
        size="large"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                className="close"
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={onCancel}
              />
            }
          >
            {i18n.getString(_TL_('Add or remove tiles'))}
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody>
          <>{i18n.getString(_TL_('Select what tiles you would like to see in this overview.'))}</>
          <div className={classNames('cards-container', styles.cardsContainer)}>
            {_.map(
              localCardsState,
              (card: CardConfigProps) => (<Card i18n={i18n} key={card.id} cardId={card.id} isSelected={!card.hidden} onCardSelectionChange={onCardSelectionChange} />)
            )}
          </div>
        </DrawerBody>
        <Divider className={styles.divider} />
        <DrawerFooter>
          <Button className="save" appearance="primary" onClick={onSaveClicked}>{i18n.getString(_TL_('Save'))}</Button>
          <Button className="cancel" onClick={onCancel}>{i18n.getString(_TL_('Cancel'))}</Button>
          <Body1 className="selected-count">
            {i18n.getString(tileLabel, { count: selectedCount })}
          </Body1>
        </DrawerFooter>
      </Drawer>
    </div >
  );
}
